import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, ShoppingCart, SlidersHorizontal, Search, X,
  ChevronDown, ChevronUp, Plus, Minus,
  MapPin, Globe, User, ShoppingBag, Tag, Menu, Wifi,
} from "lucide-react";

import logo from "../../../assets/app/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../redux/productSlice";
import { addToCart, decrementCartItem } from "../../../redux/cartSlice";
import { Notifications, SupportAgent, ChatBubble, ShoppingCartOutlined } from "@mui/icons-material";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import {
  electronicsColorAttributes,
  electronicsSizeAttributes,
} from "../../ATTRIBUTES/ElectronicsAttributes";

/* ═══════════════════════════════════════════════════════
   NETWORKING CATEGORY IDS
═══════════════════════════════════════════════════════ */
const NETWORKING_CATEGORY_IDS = [20000009]; // Networking

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS — deep cyan / electric signal theme
═══════════════════════════════════════════════════════ */
const T = {
  bg:            "#FFFFFF",
  bgSoft:        "#ECFEFF",
  bgMuted:       "#CFFAFE",
  border:        "#A5F3FC",
  muted:         "#0E7490",
  mutedSoft:     "#22D3EE",
  cyan:          "#06B6D4",
  cyanLight:     "#67E8F9",
  cyanDim:       "rgba(6,182,212,0.12)",
  deep:          "#0891B2",
  red:           "#E50914",
  globalRed:     "#CC0000",
  gold:          "#FEBA02",
  grad:          "linear-gradient(135deg,#083344 0%,#0891B2 50%,#06B6D4 75%,#67E8F9 100%)",
  gradText:      "linear-gradient(90deg,#06B6D4,#67E8F9)",
  navy:          "#042f3d",
  navyMid:       "#0a4a5e",
  cyanOutline:   "1.5px solid rgba(6,182,212,0.38)",
};

const GRADIENT = T.grad;
const NAVY     = T.navy;

/* ═══════════════════════════════════════════════════════
   COSMIC CSS — deep cyan / signal palette
═══════════════════════════════════════════════════════ */
const COSMIC_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { display: none; }
  input, button { font-family: inherit; }

  @keyframes nwSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes nwAurora {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes nwShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes nwOrb1 {
    0%   { transform: translate(0,0) scale(1); }
    33%  { transform: translate(60px,-36px) scale(1.11); }
    66%  { transform: translate(-28px,48px) scale(0.93); }
    100% { transform: translate(0,0) scale(1); }
  }
  @keyframes nwOrb2 {
    0%   { transform: translate(0,0); }
    33%  { transform: translate(-66px,26px) scale(1.08); }
    66%  { transform: translate(38px,-58px) scale(1.04); }
    100% { transform: translate(0,0); }
  }
  @keyframes nwOrb3 {
    0%,100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(24px,34px) scale(1.06); }
  }
  @keyframes starTwinkle {
    0%,100% { opacity:0.10; transform:scale(0.8); }
    50%     { opacity:0.90; transform:scale(1.5); }
  }
  @keyframes nw-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  @keyframes nwRing {
    0%   { transform: scale(0.6); opacity: 0.7; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes nwDash {
    0%   { stroke-dashoffset: 200; }
    100% { stroke-dashoffset: 0; }
  }

  .nw-cosmic-bg {
    background: linear-gradient(135deg,
      #020d12 0%, #062030 14%, #041520 28%,
      #020c18 42%, #041a28 58%, #062530 74%,
      #030e18 88%, #020d12 100%
    );
    background-size: 400% 400%;
    animation: nwAurora 20s ease infinite;
  }

  .nw-shimmer-title {
    background: linear-gradient(90deg,
      #06B6D4 0%, #67E8F9 18%, #fff 36%,
      #22D3EE 52%, #06B6D4 68%, #67E8F9 84%, #fff 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: nwShimmer 4s linear infinite;
  }

  .nw-grad-line {
    background: linear-gradient(90deg, #083344, #0891B2, #06B6D4, #67E8F9, #06B6D4, #0891B2, #083344);
    background-size: 300% 100%;
    animation: nwSpin 4s linear infinite;
  }

  .nw-card-hover {
    transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
  }
  .nw-card-hover:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 56px rgba(6,182,212,0.24), 0 0 0 2px rgba(6,182,212,0.45) !important;
  }

  .nw-sort-item:hover { background: #CFFAFE; }
  input::placeholder { color: rgba(103,232,249,0.40); }
`;

/* ═══════════════════════════════════════════════════════
   STARS
═══════════════════════════════════════════════════════ */
const STARS = Array.from({ length: 36 }, (_, i) => ({
  id: i,
  size: Math.random() * 2.4 + 0.5,
  x: Math.random() * 100,
  y: Math.random() * 100,
  delay: Math.random() * 8,
  dur: 2.4 + Math.random() * 5,
  cool: Math.random() > 0.42,
}));

/* ═══════════════════════════════════════════════════════
   SORT
═══════════════════════════════════════════════════════ */
type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const SORT_OPTS: { key: SortKey; label: string }[] = [
  { key: "default",    label: "Best Match"       },
  { key: "newest",     label: "New Arrivals"      },
  { key: "price_asc",  label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "name_asc",   label: "Name: A → Z"       },
];

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
const Networking: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { convertRaw, symbol } = useCurrency();

  const authUser         = useSelector((s: RootState) => s.auth.user);
  const authLoaded       = useSelector((s: RootState) => s.auth.authLoaded);
  const userId           = authUser?.userId;
  const notifications    = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount      = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const filteredListings = useSelector((s: RootState) => s.products.filteredProducts);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  const [isMobile,      setIsMobile]      = useState(window.innerWidth <= 768);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeSort,    setActiveSort]    = useState<SortKey>("default");
  const [sortOpen,      setSortOpen]      = useState(false);
  const [navOpen,       setNavOpen]       = useState(false);
  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  const [userCountry,    setUserCountry]    = useState<string | null>(null);
  const [userCity,       setUserCity]       = useState<string | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading,setLocationLoading]= useState(true);
  const [showGlobal,     setShowGlobal]     = useState(false);

  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any | null>(null);
  const [variantMode,   setVariantMode]   = useState<"fashion" | "beauty" | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize,  setSelectedSize]  = useState<number | null>(null);
  const [variantQty,    setVariantQty]    = useState(1);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null)); return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const getVariantMode = (item: any): "fashion" | "beauty" | null => {
    if (!item.variants || !Object.keys(item.variants).length) return null;
    if (Object.values(item.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length)) return "fashion";
    if (Object.values(item.variants).some((v: any) => v.imageUrl && (!v.sizeIds || !v.sizeIds.length))) return "beauty";
    return null;
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) {
      setVariantItem(item); setVariantMode(getVariantMode(item));
      setSelectedColor(null); setSelectedSize(null); setVariantQty(1); setVariantOpen(true);
      return;
    }
    dispatch(addToCart({ listingId: item.listingId, name: item.name!, price: item.rate!, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
  };

  const canContinue =
    variantMode === null ||
    (variantMode === "fashion" && selectedColor !== null && selectedSize !== null && variantQty > 0) ||
    (variantMode === "beauty"  && selectedColor !== null && variantQty > 0);

  const previewColorId = useMemo(() => {
    if (!variantItem || !variantMode) return null;
    const colors = variantItem.attributes?.[variantMode === "beauty" ? 10001 : 20002];
    if (!Array.isArray(colors) || !colors.length) return null;
    return selectedColor ?? colors[0];
  }, [variantItem, variantMode, selectedColor]);

  useEffect(() => {
    if (variantOpen && variantMode === "beauty" && variantItem && selectedColor === null) {
      const colors = variantItem.attributes?.[10001];
      if (Array.isArray(colors) && colors.length) setSelectedColor(colors[0]);
    }
  }, [variantOpen, variantMode, variantItem, selectedColor]);

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!, price: variantItem.rate!,
      photo: variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selectedColor!, ...(variantMode === "fashion" && { sizeId: selectedSize! }),
      qty: variantQty, hostId: variantItem.hostId, brandId: variantItem.brandId,
    }));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); setVariantQty(1);
    if (toCheckout) navigate(`/metacheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0) + variantQty, items: cartItems },
    });
  };

  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/productcheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0), totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0), items: cartItems },
    });
  };

  const goToDetails = (id: string) => navigate(`/productdetails/${id}`);

  const fmtPrice = (rawRate: number) => {
    const converted = convertRaw(rawRate, "EUR");
    return `${symbol}${converted.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  const networkingProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;
      if (!NETWORKING_CATEGORY_IDS.includes(l.categoryId)) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    return list;
  }, [filteredListings, searchQuery, activeSort]);

  /* ── HEADER ── */
  const Header = () => (
    <div style={{ position: "sticky", top: 0, zIndex: 200 }}>
      <div className="nw-cosmic-bg" style={{ position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {STARS.slice(0, 16).map(s => (
            <div key={s.id} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, borderRadius: "50%", background: s.cool ? "rgba(103,232,249,0.85)" : "rgba(255,255,255,0.65)", animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
          ))}
        </div>
        <div style={{ position: "absolute", top: "-50%", left: "6%", width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle,rgba(6,182,212,0.42) 0%,transparent 70%)", filter: "blur(36px)", animation: "nwOrb1 14s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "-38%", right: "10%", width: 170, height: 170, borderRadius: "50%", background: "radial-gradient(circle,rgba(34,211,238,0.30) 0%,transparent 70%)", filter: "blur(28px)", animation: "nwOrb2 17s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "-24%", left: "44%", width: 130, height: 130, borderRadius: "50%", background: "radial-gradient(circle,rgba(103,232,249,0.20) 0%,transparent 70%)", filter: "blur(22px)", animation: "nwOrb3 11s ease-in-out infinite", pointerEvents: "none" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 6px", position: "relative", zIndex: 2 }}>
          <div onClick={() => navigate(`/producthome/${userId}`)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
            <div style={{ padding: 2, borderRadius: 10, background: T.grad, display: "flex" }}>
              <img src={logo} alt="Metarelm" style={{ width: 26, height: 26, borderRadius: 8, background: "#fff", objectFit: "contain", padding: 3 }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
            </div>
          </div>
          <span className="nw-shimmer-title" style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? 16 : 20, fontWeight: 700, letterSpacing: -0.3, flexShrink: 0 }}>Networking</span>
          {!locationLoading && !locationDenied && userCountry && (
            <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(103,232,249,.15)", border: `1px solid ${showGlobal ? "rgba(255,255,255,.18)" : "rgba(103,232,249,.45)"}`, borderRadius: 8, padding: "5px 10px", color: showGlobal ? "rgba(103,232,249,.55)" : T.cyanLight, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
              {showGlobal ? <Globe size={11} /> : <MapPin size={11} />}
              <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry}</span>
            </button>
          )}
          {locationLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.06)", borderRadius: 8, padding: "5px 10px", flexShrink: 0 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.cyanLight, animation: "nw-pulse 1.2s infinite" }} />
              <span style={{ fontSize: 10, color: "rgba(103,232,249,.45)" }}>Locating…</span>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={() => navigate(`/MetaCommerce/${userId}`)} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(103,232,249,.25)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}><Home size={16} /></button>
          <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => navigate(`/notifications/${userId}`)}>
            <Notifications style={{ fontSize: 22, color: T.cyanLight }} />
            {unreadCount > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.globalRed, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadCount}</span>}
          </div>
          <button onClick={goToCheckout} style={{ position: "relative", background: cartTotal > 0 ? T.cyan : "rgba(255,255,255,.07)", border: `1px solid ${cartTotal > 0 ? "transparent" : "rgba(103,232,249,.25)"}`, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            <ShoppingCart size={16} />
            {!isMobile && cartTotal > 0 && <span>{cartTotal}</span>}
            {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
          </button>
          <button onClick={() => setNavOpen(v => !v)} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(103,232,249,.25)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}>{navOpen ? <X size={16} /> : <Menu size={16} />}</button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 16px 12px", position: "relative", zIndex: 2 }}>
          <div style={{ display: "flex", alignItems: "center", flex: 1, background: "rgba(103,232,249,.10)", borderRadius: 10, overflow: "hidden", border: `1.5px solid ${searchFocused ? "rgba(103,232,249,.70)" : "rgba(103,232,249,.22)"}`, backdropFilter: "blur(8px)", boxShadow: searchFocused ? "0 0 0 3px rgba(6,182,212,.20)" : "none", transition: "border-color .2s,box-shadow .2s" }}>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} placeholder="Search routers, switches, modems, extenders…" style={{ flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent" }} />
            {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "rgba(103,232,249,.5)" }}><X size={13} /></button>}
            <div style={{ width: 42, height: 42, background: T.grad, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Search size={15} color="#fff" /></div>
          </div>
          <button ref={sortBtnRef} onClick={toggleSort} style={{ display: "flex", alignItems: "center", gap: 5, background: sortOpen ? T.cyan : "rgba(255,255,255,.07)", border: `1px solid ${sortOpen ? T.cyan : "rgba(103,232,249,.25)"}`, borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
            <SlidersHorizontal size={13} />
            {!isMobile && <span>{activeSort !== "default" ? SORT_OPTS.find(o => o.key === activeSort)?.label?.split(":")[0] : "Sort"}</span>}
            {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
        </div>
        <div className="nw-grad-line" style={{ height: 2, position: "relative", zIndex: 2 }} />
      </div>

      {sortOpen && sortAnchor && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ position: "fixed", top: sortAnchor.top, left: sortAnchor.left, background: "#fff", borderRadius: 14, boxShadow: "0 20px 56px rgba(0,0,0,.22)", zIndex: 9999, minWidth: 200, overflow: "hidden", border: T.cyanOutline }}>
            {SORT_OPTS.map(opt => (
              <div key={opt.key} className="nw-sort-item" onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }} style={{ padding: "11px 18px", fontSize: 13, fontWeight: activeSort === opt.key ? 700 : 500, color: activeSort === opt.key ? T.cyan : NAVY, background: activeSort === opt.key ? T.bgMuted : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #A5F3FC" }}>
                {opt.label}{activeSort === opt.key && <span style={{ color: T.cyan }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}

      <AnimatePresence>
        {navOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 599 }} onClick={() => setNavOpen(false)} />
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} style={{ position: "fixed", top: 70, right: 16, width: 220, zIndex: 600, background: "#fff", borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,.20)", border: T.cyanOutline, overflow: "hidden" }}>
              {[
                { label: "Profile", icon: <User size={14} />, path: `/profile/${userId}` },
                { label: "Orders",  icon: <ShoppingBag size={14} />, path: `/MetaClientDashboard/${userId}` },
                { label: "Sell",    icon: <Tag size={14} />, path: `/metaonboarding/${userId}` },
                { label: "Support", icon: <SupportAgent style={{ fontSize: 14 }} />, path: `/CustomerSupport/${userId}` },
                { label: "Chat",    icon: <ChatBubble style={{ fontSize: 14 }} />, path: "/MessagesPreview" },
              ].map(item => (
                <button key={item.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: NAVY, borderBottom: "1px solid #A5F3FC", background: "none", width: "100%", textAlign: "left", border: "none" }} onClick={() => { navigate(item.path); setNavOpen(false); }}>
                  <span style={{ color: T.cyan }}>{item.icon}</span>{item.label}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );

  /* ── HERO ── */
  const HeroStrip = () => (
    <div style={{ position: "relative", overflow: "hidden", background: "#020d12", padding: isMobile ? "28px 16px 24px" : "44px 36px 36px" }}>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {STARS.map(s => (
          <div key={s.id} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, borderRadius: "50%", background: s.cool ? "rgba(103,232,249,0.80)" : "rgba(255,255,255,0.55)", animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
        ))}
      </div>
      <div style={{ position: "absolute", top: "12%", left: "3%", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle,rgba(6,182,212,0.20) 0%,transparent 70%)", filter: "blur(72px)", animation: "nwOrb1 16s ease-in-out infinite", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-18%", right: "5%", width: 290, height: 290, borderRadius: "50%", background: "radial-gradient(circle,rgba(34,211,238,0.16) 0%,transparent 70%)", filter: "blur(62px)", animation: "nwOrb2 21s ease-in-out infinite", pointerEvents: "none" }} />

      {/* WiFi rings decorative */}
      {!isMobile && (
        <div style={{ position: "absolute", right: 48, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", zIndex: 1 }}>
          {[80, 120, 160].map((r, i) => (
            <div key={i} style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: r, height: r, borderRadius: "50%", border: `1.5px solid rgba(6,182,212,${0.22 - i * 0.06})`, animation: `nwRing ${2 + i * 0.7}s ease-out ${i * 0.5}s infinite` }} />
          ))}
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 8, height: 8, borderRadius: "50%", background: T.cyan, boxShadow: `0 0 12px ${T.cyan}` }} />
        </div>
      )}

      {/* data-flow grid */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(6,182,212,0.07) 1px,transparent 1px),linear-gradient(90deg,rgba(6,182,212,0.07) 1px,transparent 1px)", backgroundSize: "38px 38px", pointerEvents: "none", zIndex: 1 }} />

      <div style={{ position: "relative", zIndex: 2 }}>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 24, height: 2, background: T.cyan, borderRadius: 1 }} />
          <p style={{ fontSize: 9, letterSpacing: "0.32em", color: T.cyanLight, fontFamily: "'DM Sans',sans-serif", fontWeight: 700 }}>📡 NETWORKING COLLECTION</p>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="nw-shimmer-title" style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? "clamp(26px,7vw,38px)" : "clamp(38px,4.5vw,62px)", fontWeight: 700, lineHeight: 1.06, letterSpacing: "-0.02em", marginBottom: 10 }}>
          Stay<br /><em style={{ fontStyle: "italic", fontWeight: 400 }}>Connected</em>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} style={{ fontSize: 13, color: "rgba(103,232,249,0.55)", fontFamily: "'DM Sans',sans-serif", fontWeight: 300, maxWidth: 500 }}>
          {locationDenied ? "Showing all global networking gear" : showGlobal ? "Browsing worldwide routers, switches & modems" : userCity ? `Networking gear near ${userCity}` : "Discovering networking products near you"}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} style={{ display: "flex", gap: 28, marginTop: 22, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: isMobile ? 32 : 44, fontWeight: 900, color: "#fff", fontFamily: "'Playfair Display',serif", lineHeight: 1 }}>{networkingProducts.length}</span>
              <span style={{ fontSize: 10, color: "rgba(103,232,249,.45)", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.12em" }}>LISTINGS</span>
            </div>
          </div>
          {!locationDenied && !showGlobal && userCountry && (
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: isMobile ? 20 : 28, fontWeight: 900, color: T.cyanLight, fontFamily: "'Playfair Display',serif", lineHeight: 1 }}>{userCountry}</span>
                <span style={{ fontSize: 10, color: "rgba(103,232,249,.45)", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.12em" }}>LOCAL</span>
              </div>
            </div>
          )}
          <div style={{ marginLeft: "auto", opacity: 0.10 }}><Wifi size={isMobile ? 60 : 86} color="#06B6D4" /></div>
        </motion.div>
      </div>
    </div>
  );

  /* ── CARD ── */
  const NetworkCard = ({ item }: { item: any }) => {
    const qty = getQty(item.listingId);
    const price = fmtPrice(item.rate ?? 0);
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const [hovered, setHovered] = useState(false);

    if (isMobile) {
      return (
        <motion.div whileTap={{ scale: 0.95 }} style={{ borderRadius: 18, overflow: "hidden", border: T.cyanOutline, boxShadow: `0 4px 20px ${T.cyanDim}`, background: "#fff", cursor: "pointer" }} onClick={() => goToDetails(item.listingId)}>
          <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: "linear-gradient(135deg,rgba(6,182,212,.18),rgba(6,182,212,.06))" }}>
            {item.photos?.[0] ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Wifi size={34} color={T.cyan} /></div>}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F2,transparent 50%)` }} />
            {discount && <div style={{ position: "absolute", top: 7, left: 7, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 6, zIndex: 5 }}>{discount}% OFF</div>}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "8px 10px 10px", zIndex: 3, pointerEvents: "none" }}>
              <div style={{ fontWeight: 800, fontSize: 10, color: "#fff", lineHeight: 1.25, marginBottom: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>{item.name}</div>
              <span style={{ color: T.red, fontWeight: 900, fontSize: 16 }}>{price}</span>
            </div>
            <div style={{ position: "absolute", top: 7, right: 7, zIndex: 10 }} onClick={e => e.stopPropagation()}>
              {qty > 0 ? (
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ display: "flex", alignItems: "center", gap: 0, background: "rgba(4,47,61,.92)", borderRadius: 20, padding: "4px 6px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(103,232,249,.3)" }}>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}><Minus size={11} color="#fff" /></button>
                  <span style={{ color: "#fff", fontWeight: 900, minWidth: 20, textAlign: "center", fontSize: 12 }}>{qty}</span>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }}><Plus size={11} color="#fff" /></button>
                </motion.div>
              ) : (
                <motion.button whileTap={{ scale: 0.88 }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }} style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ShoppingCartOutlined style={{ fontSize: 18, color: "#fff" }} /></motion.button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div className="nw-card-hover" style={{ borderRadius: 20, overflow: "hidden", border: T.cyanOutline, boxShadow: `0 6px 28px ${T.cyanDim}`, background: "#fff", cursor: "pointer" }} onClick={() => goToDetails(item.listingId)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: "linear-gradient(135deg,rgba(6,182,212,.14),rgba(6,182,212,.04))" }}>
          {item.photos?.[0] ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .38s cubic-bezier(.34,1.56,.64,1)", transform: hovered ? "scale(1.08)" : "scale(1)" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Wifi size={52} color={T.cyan} style={{ opacity: 0.3 }} /></div>}
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F5 0%,rgba(4,47,61,0.72) 38%,transparent 65%)`, pointerEvents: "none" }} />
          {discount && <div style={{ position: "absolute", top: 8, left: 8, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 7, zIndex: 5 }}>{discount}% OFF</div>}
          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ display: "flex", alignItems: "center", background: "rgba(4,47,61,.92)", borderRadius: 22, padding: "3px 5px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(103,232,249,.3)", gap: 0 }}>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}><Minus size={12} color="#fff" /></button>
                <span style={{ color: "#fff", fontWeight: 900, minWidth: 24, textAlign: "center", fontSize: 14 }}>{qty}</span>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }}><Plus size={12} color="#fff" /></button>
              </motion.div>
            ) : (
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }} style={{ width: 48, height: 48, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 3px 14px rgba(229,9,20,.55)" }}><ShoppingCartOutlined style={{ fontSize: 22, color: "#fff" }} /></motion.button>
            )}
          </div>
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 14px 14px", zIndex: 3, pointerEvents: "none" }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#fff", lineHeight: 1.25, marginBottom: 5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", letterSpacing: -0.2 }}>{item.name}</div>
            <span style={{ color: T.red, fontWeight: 900, fontSize: 22, letterSpacing: -0.5, lineHeight: 1 }}>{price}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  /* ── VARIANT MODAL ── */
  const VariantModal = () => (
    <AnimatePresence>
      {variantOpen && variantItem && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setVariantOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }} onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "28px 28px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ width: 44, height: 4, borderRadius: 2, background: "#A5F3FC", margin: "0 auto 22px" }} />
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              {variantItem.photos?.[0] && (
                <div style={{ width: 72, height: 72, borderRadius: 14, overflow: "hidden", background: "#ECFEFF", flexShrink: 0 }}>
                  <img src={variantItem.variants?.[previewColorId!]?.imageUrl ?? variantItem.photos[0]} alt={variantItem.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
              )}
              <div>
                <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 15, color: NAVY }}>{variantItem.name}</h3>
                <span style={{ color: T.red, fontWeight: 900, fontSize: 18 }}>{fmtPrice(variantItem.rate ?? 0)}</span>
              </div>
            </div>
            {variantMode && (
              <>
                <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>{variantMode === "beauty" ? "Select Shade" : "Select Color"}</h4>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  {Object.keys(variantItem.variants || {}).map(id => {
                    const colorId = Number(id);
                    const color = electronicsColorAttributes.values.find((v: any) => v.id === colorId);
                    return (
                      <button key={colorId} onClick={() => { setSelectedColor(colorId); if (variantMode === "fashion") setSelectedSize(null); }} style={{ width: 34, height: 34, borderRadius: "50%", border: selectedColor === colorId ? `3px solid ${NAVY}` : "2px solid #ddd", background: color?.hexCodes?.[0] ?? "#eee", cursor: "pointer", transform: selectedColor === colorId ? "scale(1.15)" : "scale(1)", boxShadow: selectedColor === colorId ? `0 0 0 2px #fff,0 0 0 4px ${T.cyan}` : "none", transition: "all .15s" }} />
                    );
                  })}
                </div>
                {variantMode === "fashion" && selectedColor !== null && (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>Select Size</h4>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {(variantItem.variants?.[selectedColor]?.sizeIds ?? []).map((sizeId: number) => {
                        const size = electronicsSizeAttributes.values.find((v: any) => v.id === sizeId);
                        return <button key={sizeId} onClick={() => setSelectedSize(sizeId)} style={{ padding: "8px 16px", borderRadius: 10, border: `2px solid ${selectedSize === sizeId ? NAVY : "#A5F3FC"}`, fontWeight: 700, fontSize: 13, cursor: "pointer", background: selectedSize === sizeId ? NAVY : "#fff", color: selectedSize === sizeId ? "#fff" : NAVY, transition: "all .15s" }}>{size?.name}</button>;
                      })}
                    </div>
                  </>
                )}
              </>
            )}
            {selectedColor !== null && (variantMode === "beauty" || (variantMode === "fashion" && selectedSize !== null)) && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 20, background: "#ECFEFF", borderRadius: 14, padding: "12px 16px" }}>
                <button onClick={() => setVariantQty(q => Math.max(1, q - 1))} style={{ width: 38, height: 38, borderRadius: 10, border: "2px solid #A5F3FC", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" }}><Minus size={14} color={NAVY} /></button>
                <span style={{ fontSize: 22, fontWeight: 900, color: NAVY, minWidth: 30, textAlign: "center" }}>{variantQty}</span>
                <button onClick={() => setVariantQty(q => q + 1)} style={{ width: 38, height: 38, borderRadius: 10, border: `2px solid ${T.cyan}`, background: T.cyan, display: "grid", placeItems: "center", cursor: "pointer" }}><Plus size={14} color="#fff" /></button>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(false)} style={{ flex: 1, padding: 14, borderRadius: 14, border: `2px solid ${canContinue ? NAVY : "#A5F3FC"}`, background: canContinue ? NAVY : "#ECFEFF", color: canContinue ? "#fff" : T.muted, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Add to Cart</button>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(true)} style={{ flex: 1, padding: 14, borderRadius: 14, border: "none", background: canContinue ? GRADIENT : "#ECFEFF", color: canContinue ? "#fff" : T.muted, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Buy Now</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bgSoft, paddingBottom: 32, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{COSMIC_CSS}</style>
      <Header />
      <HeroStrip />
      <div style={{ padding: isMobile ? "20px 12px" : "28px 28px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 4, height: 22, borderRadius: 2, background: GRADIENT }} />
            <span style={{ fontWeight: 900, fontSize: 15, color: NAVY }}>{locationDenied ? "All Networking" : showGlobal ? "Global Networking" : userCity ? `Networking near ${userCity}` : userCountry ?? "All Networking"}</span>
            <span style={{ fontSize: 12, color: T.muted, background: T.bgMuted, padding: "2px 9px", borderRadius: 20 }}>{networkingProducts.length}</span>
          </div>
          {!locationDenied && userCountry && (
            <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#CFFAFE" : T.bgSoft, borderRadius: 20, padding: "5px 13px", border: `1.5px solid ${showGlobal ? "#A5F3FC" : T.cyanLight}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.muted : T.cyan, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
            </button>
          )}
        </div>
        {networkingProducts.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(210px,1fr))", gap: isMobile ? 12 : 18 }}>
            {networkingProducts.map((item: any) => <NetworkCard key={item.listingId} item={item} />)}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "72px 24px" }}>
            <div style={{ display: "inline-block", marginBottom: 24 }}>
              <div style={{ width: 88, height: 88, borderRadius: "50%", background: GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(6,182,212,.35)" }}>
                <Wifi size={40} color="#fff" />
              </div>
            </div>
            <h3 style={{ fontWeight: 900, color: NAVY, marginBottom: 8, fontSize: 18, fontFamily: "'Playfair Display',serif" }}>{locationDenied ? "Location needed" : "No networking products found"}</h3>
            <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local networking gear." : searchQuery ? `No results for "${searchQuery}"` : "No networking products listed in your area yet."}</p>
            <button onClick={() => { setSearchQuery(""); setActiveSort("default"); if (!showGlobal) handleToggleGlobal(); }} style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: GRADIENT, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>Browse Global Networking</button>
          </div>
        )}
      </div>
      <VariantModal />
    </div>
  );
};

export default Networking;
